import { api, PaginationResponse } from './api';
import { UserEntity } from './userApi';

export enum EventStatus {
  DRAFT = 0,
  PUBLISHED = 1,
  CANCELLED = 2,
  COMPLETED = 3,
}

export const EventStatusLabel: Record<EventStatus, string> = {
  [EventStatus.DRAFT]: 'Draft',
  [EventStatus.PUBLISHED]: 'Published',
  [EventStatus.CANCELLED]: 'Cancelled',
  [EventStatus.COMPLETED]: 'Completed',
};

export const EventStatusColor: Record<EventStatus, string> = {
  [EventStatus.DRAFT]: 'gray',
  [EventStatus.PUBLISHED]: 'green',
  [EventStatus.CANCELLED]: 'red',
  [EventStatus.COMPLETED]: 'blue',
};

export enum EventMatchJobStatus {
  NOT_STARTED = 0,
  IN_PROGRESS = 1,
  COMPLETED = 2,
  FAILED = 3,
}

export const EventMatchJobStatusLabel: Record<EventMatchJobStatus, string> = {
  [EventMatchJobStatus.NOT_STARTED]: 'Not started',
  [EventMatchJobStatus.IN_PROGRESS]: 'In progress',
  [EventMatchJobStatus.COMPLETED]: 'Completed',
  [EventMatchJobStatus.FAILED]: 'Failed',
};

export const EventMatchJobStatusColor: Record<EventMatchJobStatus, string> = {
  [EventMatchJobStatus.NOT_STARTED]: 'gray',
  [EventMatchJobStatus.IN_PROGRESS]: 'orange',
  [EventMatchJobStatus.COMPLETED]: 'green',
  [EventMatchJobStatus.FAILED]: 'red',
};

export enum EventUserStatus {
  REGISTERED = 0,
  CONFIRMED = 1,
  CANCELLED = 2,
  ATTENDED = 3,
  INVITED = 4,
  WAITLISTED = 5,
}

export const EventUserStatusLabel: Record<EventUserStatus, string> = {
  [EventUserStatus.REGISTERED]: 'Registered',
  [EventUserStatus.CONFIRMED]: 'Confirmed',
  [EventUserStatus.CANCELLED]: 'Cancelled',
  [EventUserStatus.ATTENDED]: 'Attended',
  [EventUserStatus.INVITED]: 'Invited',
  [EventUserStatus.WAITLISTED]: 'Waitlisted',
};

export enum EventAudience {
  ALL = 0,
  TARGETED = 1,
  CODE = 2,
}

export const EventAudienceLabel: Record<EventAudience, string> = {
  [EventAudience.ALL]: 'All users',
  [EventAudience.TARGETED]: 'Targeted (invite-only)',
  [EventAudience.CODE]: 'Join code',
};

export interface EventEntity {
  id: string;
  title: string;
  description: string;
  startAt: string;
  endAt?: string | null;
  location: string;
  /** Shared seat limit. With a per-side cap set, it only covers guests that
   *  cap doesn't reach — see maleCapacity/femaleCapacity. */
  capacity?: number | null;
  /** Per-side seat limits. Each side gets its own seats and its own waitlist,
   *  and a freed seat is offered to the next guest on that same side. */
  maleCapacity?: number | null;
  femaleCapacity?: number | null;
  status: EventStatus;
  matchJobStatus: EventMatchJobStatus;
  audience: EventAudience;
  audienceFilter?: Record<string, unknown> | null;
  /** Admin-only — only present when the caller is authenticated as admin. */
  joinCode?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InviteEventUsersFilter {
  gender?: number;
  minAge?: number;
  maxAge?: number;
  city?: string;
}

export interface EventUserEntity {
  id: number;
  eventId: string;
  userId: string;
  status: EventUserStatus;
  eventNickname?: string | null;
  selfEnrolledAt?: string | null;
  user?: UserEntity;
  /** Profile completeness percentage, computed server-side for the roster. */
  profileCompleteness?: number;
  /** Whether that percentage clears the bar event matching applies. */
  matchEligible?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FindManyEventsResponse extends PaginationResponse {
  data: EventEntity[];
}

export interface UserFacingTopic {
  title: string;
  question: string;
  type: '推荐理由' | '话题' | '硬伤提示';
}

export interface PartnerMatchResult {
  resultId: number;
  partnerId: string;
  myScore: number;
  partnerScore: number;
  myReason: string;
  topics: UserFacingTopic[];
  hasDealbreaker: boolean;
}

export interface UserMatchSummary {
  userId: string;
  matches: PartnerMatchResult[];
}

export type EnrollState =
  | 'eligible'
  | 'waitlist'
  | 'already_enrolled'
  | 'not_open'
  | 'started';

export interface EventQrCodeDto {
  eventId: string;
  title: string;
  token: string;
  payload: string;
  schemePayload: string;
  confirmedCount: number;
  waitlistCount: number;
}

/** Title + body in both languages; the server picks per recipient. */
export interface EventNotificationCopy {
  titleEn: string;
  bodyEn: string;
  titleCn: string;
  bodyCn: string;
}

export interface EventNotificationTemplate {
  key: string;
  /** The copy shipped with the server — what `reset` goes back to. */
  defaults: EventNotificationCopy;
  /** What is actually sent today: the override if there is one. */
  current: EventNotificationCopy;
  isOverridden: boolean;
  updatedAt: string | null;
}

export const FEEDBACK_TEMPLATE_KEY = 'feedback_open';

export const eventApi = api.injectEndpoints({
  endpoints: (builder) => ({
    findManyEvents: builder.query<FindManyEventsResponse, void>({
      query: () => ({ url: '/events', method: 'GET' }),
      providesTags: ['Events'],
    }),
    findOneEvent: builder.query<EventEntity, string>({
      query: (id) => ({ url: `/events/${id}`, method: 'GET' }),
      providesTags: (_r, _e, id) => [{ type: 'Events', id }],
    }),
    createEvent: builder.mutation<EventEntity, Partial<EventEntity>>({
      query: (body) => ({ url: '/events', method: 'POST', body }),
      invalidatesTags: ['Events'],
    }),
    updateEvent: builder.mutation<EventEntity, Partial<EventEntity>>({
      query: ({ id, ...body }) => ({
        url: `/events/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => ['Events', { type: 'Events', id }],
    }),
    deleteEvent: builder.mutation<void, string>({
      query: (id) => ({ url: `/events/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Events'],
    }),
    findEventUsers: builder.query<EventUserEntity[], string>({
      query: (eventId) => ({
        url: `/events/${eventId}/users`,
        method: 'GET',
      }),
      providesTags: (_r, _e, eventId) => [{ type: 'EventUsers', id: eventId }],
    }),
    addEventUser: builder.mutation<
      EventUserEntity,
      { eventId: string; userId: string; status?: EventUserStatus }
    >({
      query: ({ eventId, ...body }) => ({
        url: `/events/${eventId}/users`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { eventId }) => [
        { type: 'EventUsers', id: eventId },
        { type: 'EventMatches', id: eventId },
      ],
    }),
    updateEventUser: builder.mutation<
      EventUserEntity,
      {
        eventId: string;
        eventUserId: number;
        status?: EventUserStatus;
        eventNickname?: string | null;
      }
    >({
      query: ({ eventId, eventUserId, ...body }) => ({
        url: `/events/${eventId}/users/${eventUserId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { eventId }) => [
        { type: 'EventUsers', id: eventId },
      ],
    }),
    removeEventUser: builder.mutation<
      void,
      { eventId: string; eventUserId: number }
    >({
      query: ({ eventId, eventUserId }) => ({
        url: `/events/${eventId}/users/${eventUserId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { eventId }) => [
        { type: 'EventUsers', id: eventId },
        { type: 'EventMatches', id: eventId },
      ],
    }),
    inviteEventUsers: builder.mutation<
      { invited: number; alreadyPresent: number },
      { eventId: string; userIds?: string[]; filter?: InviteEventUsersFilter }
    >({
      query: ({ eventId, ...body }) => ({
        url: `/events/${eventId}/invite`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { eventId }) => [
        { type: 'EventUsers', id: eventId },
        { type: 'Events', id: eventId },
      ],
    }),
    updateEventUsers: builder.mutation<
      { updated: number },
      { eventId: string; ids: number[]; status: EventUserStatus }
    >({
      query: ({ eventId, ids, status }) => ({
        url: `/events/${eventId}/users`,
        method: 'PATCH',
        body: { ids, status },
      }),
      invalidatesTags: (_r, _e, { eventId }) => [
        { type: 'EventUsers', id: eventId },
      ],
    }),
    removeEventUsers: builder.mutation<
      void,
      { eventId: string; ids: number[] }
    >({
      query: ({ eventId, ids }) => ({
        url: `/events/${eventId}/users`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_r, _e, { eventId }) => [
        { type: 'EventUsers', id: eventId },
        { type: 'EventMatches', id: eventId },
      ],
    }),
    findEventMatchResultsByUser: builder.query<
      UserMatchSummary,
      { eventId: string; userId: string }
    >({
      query: ({ eventId, userId }) => ({
        url: `/events/${eventId}/match-results/${userId}`,
        method: 'GET',
      }),
      providesTags: (_r, _e, { eventId, userId }) => [
        { type: 'EventMatches', id: `${eventId}-${userId}` },
        { type: 'EventMatches', id: eventId },
      ],
    }),
    // Match one guest against everyone else on the roster — the incremental
    // counterpart to Run match, for someone who joined late or whose profile
    // only just cleared the bar.
    matchEventUser: builder.mutation<
      {
        status:
          | 'ok'
          | 'not_participant'
          | 'not_eligible'
          | 'unpairable'
          | 'busy';
        paired: number;
        alreadyMatched: number;
        notified: number;
      },
      { eventId: string; userId: string }
    >({
      query: ({ eventId, userId }) => ({
        url: `/events/${eventId}/users/${userId}/match`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { eventId }) => [
        { type: 'Events', id: eventId },
        { type: 'EventUsers', id: eventId },
        { type: 'EventMatches', id: eventId },
      ],
    }),
    runEventMatch: builder.mutation<void, string>({
      query: (eventId) => ({
        url: `/events/${eventId}/event-match/run`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, eventId) => [
        'Events',
        { type: 'Events', id: eventId },
        { type: 'EventMatches', id: eventId },
      ],
    }),
    // Admin-editable copy for the post-event feedback push. The server holds
    // the shipped default and treats a saved row as an override, so `reset`
    // deletes rather than writes the default back.
    fetchEventNotificationTemplate: builder.query<
      EventNotificationTemplate,
      string
    >({
      query: (key) => ({
        url: `/events/notification-templates/${key}`,
        method: 'GET',
      }),
      providesTags: (_r, _e, key) => [
        { type: 'EventTemplates' as const, id: key },
      ],
    }),
    saveEventNotificationTemplate: builder.mutation<
      EventNotificationTemplate,
      { key: string; copy: EventNotificationCopy }
    >({
      query: ({ key, copy }) => ({
        url: `/events/notification-templates/${key}`,
        method: 'PUT',
        body: copy,
      }),
      invalidatesTags: (_r, _e, { key }) => [
        { type: 'EventTemplates' as const, id: key },
      ],
    }),
    resetEventNotificationTemplate: builder.mutation<
      EventNotificationTemplate,
      string
    >({
      query: (key) => ({
        url: `/events/notification-templates/${key}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, key) => [
        { type: 'EventTemplates' as const, id: key },
      ],
    }),
    // Post-event push to every seated guest: the like/pass window is open.
    // Sent by hand, and refused server-side outside that window.
    notifyEventFeedback: builder.mutation<{ notified: number }, string>({
      query: (eventId) => ({
        url: `/events/${eventId}/notify-feedback`,
        method: 'POST',
      }),
    }),
    redoEventPairMatch: builder.mutation<
      void,
      {
        eventId: string;
        userId1: string;
        userId2: string;
        topicFineTune?: string;
      }
    >({
      query: ({ eventId, userId1, userId2, topicFineTune }) => ({
        url: `/events/${eventId}/event-match/pair`,
        method: 'POST',
        body: { userId1, userId2, topicFineTune },
      }),
      invalidatesTags: (_r, _e, { eventId }) => [
        { type: 'EventMatches', id: eventId },
      ],
    }),
    findEventQr: builder.query<EventQrCodeDto, string>({
      query: (eventId) => ({ url: `/events/${eventId}/qr`, method: 'GET' }),
      providesTags: (_r, _e, eventId) => [
        { type: 'Events', id: `${eventId}-qr` },
      ],
    }),
  }),
});

export const {
  useFindManyEventsQuery,
  useFindOneEventQuery,
  useLazyFindOneEventQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
  useFindEventUsersQuery,
  useAddEventUserMutation,
  useInviteEventUsersMutation,
  useUpdateEventUserMutation,
  useRemoveEventUserMutation,
  useUpdateEventUsersMutation,
  useRemoveEventUsersMutation,
  useFindEventMatchResultsByUserQuery,
  useRunEventMatchMutation,
  useMatchEventUserMutation,
  useNotifyEventFeedbackMutation,
  useFetchEventNotificationTemplateQuery,
  useSaveEventNotificationTemplateMutation,
  useResetEventNotificationTemplateMutation,
  useRedoEventPairMatchMutation,
  useFindEventQrQuery,
} = eventApi;
